import { create } from 'zustand';
import type { Coordinates } from '@/types';

// Local UI state for the "reachable area" (isochrone) explore mode. Kept in its
// own tiny store — mirroring trafficStore — so the feature stays self-contained
// within components/map: the rail toggle (MapControls), the origin-pick guard
// (MapContainer's click handler) and the renderer/panel (IsochroneLayer) are
// siblings that share only this state.
//
// Not persisted: an on-demand, session-only view that defaults OFF.

// Isochrone travel modes the backend actually supports for this feature
// (normalizeProfile maps these to car/cycling/walking). transit/truck are
// intentionally excluded from the reachability explorer.
export type IsochroneProfile = 'driving' | 'cycling' | 'walking';

// Default time budgets (minutes), Mappls/HERE-style 5/10/15 presets.
export const DEFAULT_TIMES = [5, 10, 15];

interface IsochroneState {
  active: boolean; // isochrone mode on (panel visible)
  picking: boolean; // waiting for a map click to set the origin
  origin: Coordinates | null;
  profile: IsochroneProfile;
  times: number[]; // minutes, kept sorted ascending, de-duped

  setActive: (active: boolean) => void;
  toggleActive: () => void;
  setPicking: (picking: boolean) => void;
  setOrigin: (origin: Coordinates | null) => void;
  setProfile: (profile: IsochroneProfile) => void;
  setTimes: (times: number[]) => void;
  toggleTime: (minutes: number) => void;
  reset: () => void;
}

const sortTimes = (times: number[]) =>
  Array.from(new Set(times.filter((t) => t > 0))).sort((a, b) => a - b);

export const useIsochroneStore = create<IsochroneState>((set) => ({
  active: false,
  picking: false,
  origin: null,
  profile: 'driving',
  times: [...DEFAULT_TIMES],

  setActive: (active) =>
    set((s) =>
      active
        ? { active: true, picking: s.origin == null }
        : { active: false, picking: false }
    ),
  toggleActive: () =>
    set((s) =>
      s.active
        ? { active: false, picking: false }
        : { active: true, picking: s.origin == null }
    ),
  setPicking: (picking) => set({ picking }),
  // Setting an origin ends the pick; clearing it re-arms picking.
  setOrigin: (origin) => set({ origin, picking: origin == null }),
  setProfile: (profile) => set({ profile }),
  setTimes: (times) => set({ times: sortTimes(times) }),
  toggleTime: (minutes) =>
    set((s) => {
      const has = s.times.includes(minutes);
      const next = has
        ? s.times.filter((t) => t !== minutes)
        : [...s.times, minutes];
      // Never allow an empty budget set — keep at least one.
      return { times: next.length ? sortTimes(next) : s.times };
    }),
  reset: () => set({ origin: null, picking: true, times: [...DEFAULT_TIMES] }),
}));
